import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [showFullDesc, setShowFullDesc] = useState(false);

  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return <NotFound />;
  }

  const relatedComics = getRelatedComics(comic.id, 4);
  const pageTitle = `${comic.title} - ${comic.category}韩国漫画 - 桃桃漫画网`;
  const pageDesc = `${comic.title}：${comic.description.slice(0, 120)}... 在线免费阅读完整${comic.chapters}话，桃桃漫画网为您提供最佳阅读体验。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '桃桃漫画网',
      logo: {
        '@type': 'ImageObject',
        url: typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: typeof window !== 'undefined' ? `${window.location.origin}/category/${comic.categorySlug}` : '',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}可以在线免费阅读吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，${comic.title}在桃桃漫画网上完全免费，无需注册即可在线阅读全部${comic.chapters}话内容。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}目前状态为${comic.status}，${comic.status === '连载中' ? '定期更新最新章节，建议关注桃桃漫画网获取更新通知。' : '已经完结，您可以一次性阅读全部内容。'}`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}属于什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}属于${comic.category}类韩国漫画，由${comic.author}创作，评分${comic.rating}分，深受读者喜爱。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},${comic.category},韩国漫画,画免费,画漫画大全,桃桃漫画网,在线阅读`}
        canonicalUrl={`/article/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        articlePublishedTime="2026-01-01"
        articleModifiedTime={comic.updateDate}
        schemaData={[articleSchema, breadcrumbSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Info Section */}
          <section className="flex flex-col lg:flex-row gap-8 px-4 md:px-6 py-10 md:py-14">
            <div className="lg:w-1/3 flex flex-col items-start gap-4">
              <span className="inline-block px-4 py-1.5 border border-secondary-300 text-secondary-700 text-sm rounded-full">
                {comic.status}
              </span>
              <div className="w-full max-w-[300px] rounded-xl overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - ${comic.category}韩国漫画封面`}
                  title={comic.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="lg:w-2/3 flex flex-col justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-3 font-heading">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {comic.author} 作品
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">{comic.rating}</div>
                    <div className="text-xs text-foreground-500">评分</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">{(comic.views / 10000).toFixed(0)}万</div>
                    <div className="text-xs text-foreground-500">人气</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">{comic.chapters}</div>
                    <div className="text-xs text-foreground-500">话数</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${comic.status === '已完结' ? 'text-primary-500' : 'text-accent-700'}`}>
                      {comic.status}
                    </div>
                    <div className="text-xs text-foreground-500">状态</div>
                  </div>
                </div>

                <div className="relative">
                  <p className={`text-sm text-foreground-700 leading-relaxed ${showFullDesc ? '' : 'line-clamp-3'}`}>
                    {comic.description}
                  </p>
                  {comic.description.length > 120 && (
                    <button
                      onClick={() => setShowFullDesc(!showFullDesc)}
                      className="text-primary-500 text-sm mt-1 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      {showFullDesc ? '收起' : '展开全部'}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <button className="bg-foreground-900 hover:bg-foreground-800 text-background-50 px-8 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                  开始阅读
                </button>
              </div>
            </div>
          </section>

          {/* Reader Reviews Section */}
          {comic.readerReviews && comic.readerReviews.length > 0 && (
            <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="lg:w-3/5">
                    <div className="flex items-center gap-2 mb-6">
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-900"></span>
                      <span className="text-xs font-medium text-foreground-700 uppercase tracking-wider">
                        读者评价
                      </span>
                    </div>
                    {comic.readerReviews.map((review) => (
                      <div key={review.id}>
                        <blockquote className="text-xl md:text-2xl font-heading italic text-foreground-900 leading-relaxed max-w-lg mb-4">
                          &ldquo;{review.content.slice(0, 80)}...&rdquo;
                        </blockquote>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-foreground-800">{review.userName}</span>
                          <span className="flex items-center gap-1 text-xs text-accent-600">
                            <i className="ri-verified-badge-fill w-4 h-4 flex items-center justify-center"></i>
                            {review.platform}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="lg:w-2/5">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20young%20woman%20reading%20manhwa%20on%20tablet%20cozy%20cafe%20warm%20lighting%20black%20and%20white%20portrait%20style%20clean%20white%20background%20minimalist%20composition%20editorial%20photography%20soft%20shadows&width=600&height=800&seq=reader-review-taotao&orientation=portrait"
                      alt="读者阅读漫画"
                      className="w-full rounded-lg object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Related Comics Section */}
          {relatedComics.length > 0 && (
            <section className="flex flex-col lg:flex-row">
              <div className="lg:w-1/2 bg-background-100 px-4 md:px-8 py-12 md:py-16 flex items-center">
                <div className="max-w-sm">
                  <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
                    同类推荐
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-2 leading-tight font-heading">
                    喜欢{comic.category}
                    <br />
                    的人也看
                  </h2>
                </div>
              </div>
              <div className="lg:w-1/2 bg-background-50 px-4 md:px-8 py-12 md:py-16 flex items-center justify-center">
                <div className="max-w-md text-center">
                  <h3 className="text-lg md:text-xl font-bold text-foreground-900 mb-3">
                    发现更多{comic.category}漫画
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    基于您的阅读偏好，为您推荐更多同类型精彩漫画作品。
                  </p>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    浏览全部
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* Related Comics Grid */}
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 font-heading">
                同类热门推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block cursor-pointer"
                    data-product-shop=""
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[5/7] mb-3">
                      <img
                        src={related.coverImage}
                        alt={`${related.title} - 同类型漫画推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="inline-block bg-background-50/90 text-foreground-700 text-xs px-2 py-0.5 rounded-full">
                          {related.status}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">评分 {related.rating}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}